import * as React from 'react';

import * as TooltipPrimitive from '@radix-ui/react-tooltip';

import { cn } from '@avodah-inc/ui/utils';

type ToolTipType = React.ComponentProps<typeof TooltipPrimitive.Root>;

type CaretProps = {
  side: 'top' | 'right' | 'bottom' | 'left' | undefined;
};

const Caret: React.FC<CaretProps> = ({ side }) => {
  const baseStyles = 'absolute w-[16px] h-[8px] animate-in fade-in-0';
  const rotationStyles: Record<string, string> = {
    top: 'border-t-[8px] border-r-[8px] border-l-[8px] border-b-0 border-transparent border-t-gray-500 bottom-[-7px] left-1/2 transform -translate-x-1/2',
    right:
      'rotate-180 border-l-[8px] border-t-[8px] border-b-[8px] border-r-0 border-transparent border-l-gray-500 -left-[15px] top-1/2 transform -translate-y-1/2',
    bottom:
      'border-b-[8px] border-r-[8px] border-l-[8px] border-t-0 border-transparent border-b-gray-500 top-[-7px] left-1/2 transform -translate-x-1/2',
    left: 'rotate-180 border-r-[8px] border-t-[8px] border-b-[8px] border-l-0 border-transparent border-r-gray-500 right-[-15px] top-1/2 transform -translate-y-1/2',
  };

  return <div className={`${baseStyles} ${rotationStyles[side || 'top']}`} />;
};

const Tooltip = ({ children, ...props }: ToolTipType) => {
  return (
    <TooltipPrimitive.Provider {...props}>
      <TooltipPrimitive.Root>{children}</TooltipPrimitive.Root>
    </TooltipPrimitive.Provider>
  );
};

const TooltipTrigger = TooltipPrimitive.Trigger;

const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content> & {
    showCaret?: boolean;
  }
>(({ className, sideOffset = 4, showCaret = false, ...props }, ref) => (
  <TooltipPrimitive.Content
    className={cn(
      'shadow-box-md animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-[999] overflow-hidden rounded-sm bg-gray-500 px-3 py-1.5 text-sm text-white',
      className,
    )}
    ref={ref}
    sideOffset={sideOffset}
    {...props}
  >
    {props.children}
    {showCaret && <Caret side={props.side} />}
  </TooltipPrimitive.Content>
));

TooltipContent.displayName = TooltipPrimitive.Content.displayName;

export { Tooltip, TooltipTrigger, TooltipContent };
